<script lang="ts">
  import { cn } from "$lib/utils";
  import { Button } from "$lib/components/ui/button";
  import { ProfilePicture, getImageUrl } from "$lib/components/basic/profilePicture";
  import { X, Mail, Phone } from "lucide-svelte";
  import { simpleHash } from "$lib/utility";
  import type { SearchResult } from "$lib/types";
  import EmailCopyDialog from "$lib/components/dialogs/copyEmailDialog.svelte";
  import { authStore } from "$lib/stores";
  import { parseCriteria, sortByCriteriaMatch } from "./helpers";

  let {
    isPanelOpen = false,
    selectedResult = null,
    type = "person",
    onPanelClose = () => {},
    isViewedByOwner = false,
    upsellCallback = () => {},
  } = $props<{
    isPanelOpen: boolean;
    selectedResult: SearchResult | null;
    type: "person" | "company";
    isViewedByOwner: boolean;
    onPanelClose: () => void;
    upsellCallback?: () => void;
  }>();

  let sortedCriteria = $derived(
    selectedResult.criteriaAndReasons ? sortByCriteriaMatch(selectedResult.criteriaAndReasons) : [],
  );

  const isPerson = $derived(type === "person");
  const displayName = $derived(
    isPerson ? `${selectedResult?.firstName} ${selectedResult?.lastName}` : selectedResult?.companyName,
  );
  const subtitle = $derived(
    isPerson
      ? selectedResult?.title && selectedResult?.companyName
        ? `${selectedResult.title} @ ${selectedResult.companyName}`
        : selectedResult?.title || selectedResult?.companyName
      : selectedResult?.domain,
  );
</script>

<div
  class={cn(
    "custom-scrollbar fixed right-0 top-0 z-50 h-full w-[360px] transform overflow-x-hidden overflow-y-scroll border-l border-black bg-theme-background-primary transition-all duration-300 dark:border-white/10 dark:bg-theme-background-tertiary",
    isPanelOpen ? "translate-x-0" : "translate-x-full",
  )}
>
  {#if selectedResult && isPanelOpen}
    <div class="sticky top-0 flex flex-col gap-4 px-2 pb-2 pt-4">
      <Button
        variant="ghost"
        size="icon"
        class="self-end !rounded-base hover:bg-theme-background-secondary"
        onclick={() => onPanelClose()}
      >
        <X class="h-4 w-4" />
      </Button>

      <!-- Detail View -->
      <div class="flex flex-col gap-3 px-3 pb-3">
        <ProfilePicture
          wrapperClass={cn("mx-auto border border-theme-text-secondary/20 p-0.5", isPerson ? "w-20 h-20" : "w-24 h-24")}
          imageUrl={getImageUrl(selectedResult)}
          fallbackImageUrl="https://www.gravatar.com/avatar/default?d=identicon&s=256"
          shape={isPerson ? "circle" : "square"}
          outline={true}
          altText={isPerson ? "Profile" : "Company Logo"}
        />

        <h2 class="text-center text-xl font-bold text-theme-text-primary">
          {displayName}
        </h2>

        {#if subtitle}
          <p class="text-center text-xs leading-snug text-theme-text-secondary">
            {subtitle}
          </p>
        {/if}

        {#if isPerson}
          <div class="mt-4">
            <h3 class="text-xs font-semibold text-theme-text-primary">Overview</h3>
            <p class="mt-1 text-xs text-theme-text-secondary">
              {"No summary available"}
            </p>
          </div>

          <div class="mt-4">
            <h3 class="text-xs font-semibold text-theme-text-primary">Contact</h3>
            <div class="mt-2 space-y-2">
              {#if selectedResult.email}
                <Button
                  class="flex h-fit w-fit gap-1.5 !rounded-base px-0 py-0 text-2xs font-normal text-gray-600 dark:text-gray-400"
                  variant="ghost"
                  onclick={async (e: Event) => {
                    try {
                      await navigator.clipboard.writeText(selectedResult.email);
                      const button = document.activeElement as HTMLButtonElement;
                      const originalContent = button.innerHTML;
                      button.innerHTML = `<svg class="text-theme-green-deep" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>Copied!`;
                      setTimeout(() => {
                        button.innerHTML = originalContent;
                      }, 2000);
                    } catch (error) {
                      console.error("Failed to copy:", error);
                    }
                  }}
                >
                  <Mail strokeWidth={1} class="max-h-3" />
                  {selectedResult.email}
                </Button>
              {:else if isViewedByOwner && selectedResult.id}
                <EmailCopyDialog
                  entityId={selectedResult?.id}
                  {type}
                  {displayName}
                  on:emailUpdate={(e) => {
                    selectedResult.email = e.detail.email;
                    authStore.updateUserCredits();
                  }}
                >
                  <Button
                    variant="ghost"
                    class="flex h-fit w-fit gap-1.5 !rounded-base px-0 py-0 text-2xs font-normal text-gray-600 dark:text-gray-400"
                  >
                    <Mail strokeWidth={1} class="max-h-3" />
                    [show email]
                  </Button>
                </EmailCopyDialog>
              {:else}
                <Button
                  class="flex h-fit w-fit gap-1.5 !rounded-base px-0 py-0 text-2xs font-normal text-gray-600 dark:text-gray-400"
                  variant="ghost"
                  onclick={upsellCallback}
                >
                  <Mail strokeWidth={1} class="max-h-3" />
                  view email
                </Button>
              {/if}

              {#if selectedResult.phone}
                <div class="flex items-center gap-2">
                  <Phone class="h-4 w-4 text-theme-text-secondary" />
                  <span class="text-xs text-theme-text-secondary">{selectedResult.phone}</span>
                </div>
              {/if}
            </div>
          </div>
        {/if}

        <!-- Shared Criteria Section -->
        {#if selectedResult.criteriaAndReasons?.length}
          <div class="mt-4">
            <h3 class="mb-1 text-xs font-semibold text-theme-text-primary">Criteria</h3>
            <ul class="space-y-2">
              {#each sortedCriteria as cr}
                <li class="flex items-start gap-2">
                  <span class={cn(cr.isMatch === "match" ? "text-green-600" : "text-red-600", "text-xs font-medium")}>
                    {cr.isMatch === "match" ? "✓" : "✗"}
                  </span>
                  <div class="text-xs text-theme-text-secondary">
                    <span class="font-medium text-theme-text-primary">{parseCriteria(cr)}</span>: {cr.reason}
                  </div>
                </li>
              {/each}
            </ul>
          </div>
        {/if}

        <!-- Shared Sources Section -->
        {#if selectedResult.sources?.length}
          <div class="mt-4">
            <h3 class="text-xs font-semibold text-theme-text-primary">Sources</h3>
            <div class="mt-2 flex flex-col gap-3">
              {#each selectedResult.sources as source}
                {#if source.url}
                  <div
                    class="rounded-base border border-theme-text-tertiary p-4 leading-tight dark:bg-theme-background-secondary"
                  >
                    <div class="flex items-start justify-between gap-2">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-xs leading-tight text-black hover:underline dark:text-theme-text-primary"
                      >
                        {source.title || source.url}
                      </a>
                      {#if isPerson && source.url.includes("linkedin")}
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path
                            d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                          />
                        </svg>
                      {/if}
                    </div>
                    {#if source.snippet}
                      <p class="mt-2 text-xs leading-relaxed text-theme-text-secondary dark:text-theme-gray-600">
                        {source.snippet}
                      </p>
                    {/if}
                  </div>
                {/if}
              {/each}
            </div>
          </div>
        {/if}

        {#if authStore.isAdmin()}
          <div class="relative mt-4">
            <h3 class="text-xs font-semibold text-theme-text-primary">Raw data</h3>

            <pre
              class="mt-2 overflow-x-auto rounded-lg bg-gray-50 p-4 font-mono text-xs leading-none dark:bg-gray-800">{#each Object.entries(selectedResult || {}) as [key, value]}<span
                  class="text-blue-600 dark:text-blue-400">{key.padEnd(12)}</span
                >: {@html typeof value === "object" && value !== null
                  ? JSON.stringify(value, null, 2)
                      .split("\n")
                      .map((line) => line.trimStart())
                      .join("\n")
                      .replace(/"(\w+)":/g, '"<span class="text-orange-600 dark:text-blue-400">$1</span>:')
                  : JSON.stringify(value)}<br />{/each}</pre>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
